import { isOrderWaitingOnSeller } from '../../transactions/transaction';
import { canConnectStockFor, hasShopProfileFrom } from './onboarding';

/**
 * Turn Redux state into the plain inputs that actionCenter and onboarding expect.
 *
 * Kept separate from both the duck and the component so the derivation can be
 * tested against realistic state shapes. These are the numbers a seller will
 * compare against the topbar bell, so being wrong here is immediately visible.
 */

/* Notification kinds, as tagged by fetchNotificationData in ducks/user.duck.
 * 'ACTIVITY' is a new sale; 'MESSAGE' is a message on an order or a sale. */
const ACTIVITY = 'ACTIVITY';
const MESSAGE = 'MESSAGE';

/**
 * Is this a paid order still waiting on the seller?
 *
 * The rule itself — an allowlist of exactly `transition/confirm-payment`, and the
 * measurements that forced it — lives in `transactions/transaction.js` as
 * `isOrderWaitingOnSeller`, because P25009-389 needs the same answer for the
 * per-listing "nya ordrar" chip. Two copies of this rule would drift, and a
 * dashboard that disagrees with the listing row it links to is the bug this epic
 * keeps fixing.
 *
 * This wrapper only unpacks a notification to the `attributes` the rule wants.
 */
export const isPaidOrder = notification => isOrderWaitingOnSeller(notification?.attributes);

/**
 * New orders the seller has not looked at.
 *
 * Only genuine, paid orders — see isPaidOrder above for why inquiries and
 * abandoned checkouts are excluded.
 */
export const countNewOrders = notifications =>
  (notifications || []).filter(
    n => n?.notificationType === ACTIVITY && n?.isUnread && isPaidOrder(n)
  ).length;

/**
 * Unread messages on the seller's OWN sales.
 *
 * Three decisions, each learned the hard way against real data.
 *
 * 1. The figure comes from the `providerHasUnreadMessages` metadata flag — the
 *    same field `/inbox/sales?filter=unread` filters on. The card links to that
 *    page, so the number has to come from the same field or the two contradict
 *    each other on screen. The obvious alternative, the `isUnread` flag on each
 *    notification, is computed from a fallback chain ending at the TRANSACTION's
 *    createdAt, so it reports "this sale was never opened" (some from 2024) as
 *    "unread message". It said 27 where the inbox said 9.
 *
 * 2. Provider side only. The seller's own purchases also have unread messages,
 *    but those belong to the buyer surface, and counting them here made the card
 *    disagree with the sales page it opens. The topbar envelope deliberately
 *    counts both roles — it opens whichever side you were last on.
 *
 * 3. It reads a COUNT the server produced, not a tally of flags on
 *    `state.user.notifications`. That last change came out of browser testing on
 *    P25009-390: the listings page said one unread message and the topbar said
 *    none. Flags can only be attached to notifications that exist, and the
 *    notification list is assembled from three unrelated 100-row pages — an
 *    unread transaction sitting beyond row 100 of the `hasMessage` page never
 *    becomes a notification, so its flag has nothing to live on and the count
 *    silently loses it.
 *
 * Takes the whole Redux state rather than the notification array, because the
 * count no longer lives in that array.
 */
export const countUnreadMessages = state => state?.user?.unreadSalesMessageCount || 0;

/** Available balance in minor units, or null when the figure is unavailable. */
export const availableBalanceCents = summary => {
  const available = summary?.balance?.available;
  return typeof available === 'number' ? available : null;
};

/**
 * Everything buildActionRows() needs.
 *
 * `stripeConnected` is passed through untouched, including when it is undefined:
 * actionCenter treats undefined as "still loading" and stays silent, which is
 * what stops a half-loaded page telling a seller their payouts are broken.
 */
export const deriveActionInput = state => {
  const user = state?.user || {};
  const dashboard = state?.SellerDashboardPage || {};
  const finance = state?.SellerFinancePage || {};
  const counts = dashboard.listingCounts;

  return {
    stripeStatus: dashboard.stripeStatus,
    stripeConnected: user.currentUser?.attributes?.stripeConnected,
    availableBalanceCents: availableBalanceCents(finance.summary),
    newOrderCount: countNewOrders(user.notifications),
    unreadMessageCount: countUnreadMessages(state),
    lowStockCount: counts?.lowStock || 0,
    draftCount: counts?.drafts || 0,
    /* Until the counts load, assume the seller is established. The alternative
     * biases towards showing "finish your first listing" prompts to sellers with
     * a hundred listings for the first second of every page load. */
    hasPublishedListing: counts ? counts.published > 0 : true,
  };
};

/** Everything buildOnboardingSteps() needs. */
export const deriveOnboardingInput = state => {
  const user = state?.user || {};
  const dashboard = state?.SellerDashboardPage || {};
  const counts = dashboard.listingCounts;
  const currentUser = user.currentUser;
  const services = currentUser?.attributes?.profile?.publicData?.services;

  return {
    /* "Ready" means connected AND nothing outstanding. A connected account that
     * Stripe is still asking questions about cannot pay out, so ticking the step
     * would be a lie. */
    stripeReady:
      currentUser?.attributes?.stripeConnected === true &&
      !!dashboard.stripeStatus &&
      dashboard.stripeStatus.detailsSubmitted === true &&
      dashboard.stripeStatus.hasExternalAccount === true &&
      (dashboard.stripeStatus.pastDue?.length || 0) === 0 &&
      (dashboard.stripeStatus.currentlyDue?.length || 0) === 0,
    addressCount: dashboard.addressCount || 0,
    serviceCount: Array.isArray(services) ? services.length : 0,
    hasPublishedListing: counts ? counts.published > 0 : false,
    hasShopProfile: hasShopProfileFrom(currentUser),
    // P25009-410 — "Anslut ditt lager" instead of "first listing".
    canConnectStock: canConnectStockFor(currentUser),
    hasCatalogue: counts ? (counts.catalogue || 0) > 0 : false,
  };
};

/** Is this account one the seller dashboard is meant for? */
export const isSellerAccount = currentUser => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  return userType === 'seller' || userType === 'business';
};
